import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { readAttribution } from "@/lib/attribution";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("ae_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ae_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("ae_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("ae_token");
        localStorage.removeItem("ae_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password, opts = {}) => {
    // BLOCK 35: forward the optional TOTP code / recovery code the login
    // page collects on the second-stage MFA input. The first call omits
    // both; the backend responds 401 "MFA code required" for accounts
    // with mfa_enabled=true, and the login page then reveals the input
    // and retries with either mfa_code OR recovery_code populated.
    const payload = { email, password };
    if (opts.mfa_code) payload.mfa_code = opts.mfa_code;
    if (opts.recovery_code) payload.recovery_code = opts.recovery_code;
    // BLOCK 170 — an optional email-verification ticket. Never a substitute
    // for the password above (already required) — it only lets login()
    // additionally stamp email_verified_at when the ticket is valid.
    if (opts.verification_ticket) payload.verification_ticket = opts.verification_ticket;
    const r = await api.post("/auth/login", payload);
    localStorage.setItem("ae_token", r.data.access_token);
    localStorage.setItem("ae_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async (payload) => {
    // BLOCK 187 — attach first-touch attribution (if any was captured);
    // server treats it as fully optional and never trusts it for role/tier.
    const r = await api.post("/auth/register", { ...payload, attribution: readAttribution() });
    localStorage.setItem("ae_token", r.data.access_token);
    localStorage.setItem("ae_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  // BLOCK 170 — brand-new seller account, created only after a real OTP
  // code verified the email (see SellerAuthGate.jsx). Role is hardcoded
  // "seller" server-side; no client-supplied role is ever accepted here.
  const createSellerAccount = async ({ email, ticket, name, password }) => {
    // BLOCK 187 — same first-touch attribution attached as /auth/register.
    const r = await api.post("/verify/create-account", {
      email, ticket, name, password, attribution: readAttribution(),
    });
    localStorage.setItem("ae_token", r.data.access_token);
    localStorage.setItem("ae_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  };

  // BLOCK 170 — for an ALREADY-authenticated user whose account predates
  // email verification. Consumes a ticket from a code sent to their own
  // account email (no re-entering email or password).
  const verifyEmailWithTicket = async (ticket) => {
    const r = await api.post("/auth/verify-email", { ticket });
    const updated = { ...user, email_verified_at: r.data.email_verified_at || new Date().toISOString() };
    localStorage.setItem("ae_user", JSON.stringify(updated));
    setUser(updated);
    return updated;
  };

  const logout = () => {
    localStorage.removeItem("ae_token");
    localStorage.removeItem("ae_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, createSellerAccount, verifyEmailWithTicket }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
